import { Typography } from "@mui/material";
import {
  LQG_THRESHOLD_ACUTE_HAZARDOUS_LB,
  LQG_THRESHOLD_HAZARDOUS_LB,
  SQG_THRESHOLD_HAZARDOUS_LB,
} from "encamp-shared/src/constants/wasteGeneratorStatusThresholds";

import { formatLbs } from "./generatorStatusDisplay";

const lqg = formatLbs(LQG_THRESHOLD_HAZARDOUS_LB);
const sqg = formatLbs(SQG_THRESHOLD_HAZARDOUS_LB);
const acute = formatLbs(LQG_THRESHOLD_ACUTE_HAZARDOUS_LB);

type Threshold = { range: string; condition?: string };

/** These ranges use the same constants as the federal, CA and WA calculators. */
function thresholdForStatus(
  status: string | null | undefined,
  state: string | null | undefined,
  acuteLbs: number | undefined,
  totalLbs: number | undefined
): Threshold | null {
  if (!status) return null;
  if (state && state !== "CA" && state !== "WA") return null;
  if (status === "LARGE_QUANTITY_GENERATOR") {
    if (acuteLbs != null && acuteLbs >= LQG_THRESHOLD_ACUTE_HAZARDOUS_LB)
      return { range: `${acute} lb/month or more of acute waste` };
    if (totalLbs != null && totalLbs >= LQG_THRESHOLD_HAZARDOUS_LB)
      return { range: `${lqg} lb/month or more` };
    return {
      range: `${lqg} lb/month or more`,
      condition: `Or ${acute} lb/month or more of acute waste`,
    };
  }
  const condition = `Acute waste below ${acute}\u00a0lb/month`;
  if (state === "CA" && status === "SMALL_QUANTITY_GENERATOR")
    return { range: `Below ${lqg} lb/month`, condition };
  if (
    (!state && status === "SMALL_QUANTITY_GENERATOR") ||
    (state === "WA" && status === "MEDIUM_QUANTITY_GENERATOR")
  )
    return { range: `${sqg} to under ${lqg} lb/month`, condition };
  if (
    (!state && status === "VERY_SMALL_QUANTITY_GENERATOR") ||
    (state === "WA" && status === "SMALL_QUANTITY_GENERATOR")
  )
    return { range: `Below ${sqg} lb/month`, condition };
  return null;
}

export function CalculationThreshold({
  status,
  state,
  acuteLbs,
  totalLbs,
}: {
  status: string | null | undefined;
  state?: string | null;
  acuteLbs?: number;
  totalLbs?: number;
}) {
  const threshold = thresholdForStatus(status, state, acuteLbs, totalLbs);
  if (!threshold) {
    return (
      <Typography variant="body2" color="text.secondary">
        Threshold unavailable
      </Typography>
    );
  }
  return (
    <>
      <Typography variant="body2">{threshold.range}</Typography>
      {threshold.condition && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {threshold.condition}
        </Typography>
      )}
    </>
  );
}
